import React, { useContext, useMemo } from 'react'
import { Grid, makeStyles } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import CloseIcon from '@material-ui/icons/Close'
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import 'moment/locale/fr'
import { WHITE } from '../../../../theme'
import styles from '../../../../styles'
import Fab from '../../../../components/fab/Fab'
import classnames from 'classnames'
import Button from '../../../../components/button/Button'
import { ChevronRight } from '@material-ui/icons'
import Loader from '../../../../components/loader/Loader'
import { formattageHeure } from '../utils/calendrierVisiteUtils'
import { CalendrierContext } from '../context/CalendrierContext'
import { Theme } from '@material-ui/core/styles/createMuiTheme'
import { ResponsiveContext } from '../../../../utils/context/responsiveContext'
import { formatageDate } from '../../../../utils/formatDate'

const useStyles = makeStyles<Theme, { isLargeScreen: boolean }>(
	(theme) => ({
		calendrierSubContainer: {
			marginBottom: 'auto'
		},
		enteteContainer: {
			marginBottom: props => props.isLargeScreen ? 41 : 10,
			height: props => props.isLargeScreen ? 48 : 'fit-content',
			alignItems: props => props.isLargeScreen ? 'unset' : 'start'
		},
		titre: {
			...styles(theme).text0,
			fontSize: 23,
			lineHeight: '33px'
		},
		iconStyle: {
			maxWidth: 'fit-content',

			'& svg': {
				'&:hover': {
					background: theme.palette.blue['90']
				},
				padding: 12,
				borderRadius: '100%',
				color: WHITE,
				backgroundColor: theme.palette.blue.main,
				cursor: 'pointer'
			}
		},
		timePicker: {
			maxHeight: 469,
			flexFlow: props => props.isLargeScreen ? 'unset' : 'column'
		},
		dateContainer: {
			justifyContent: 'center',

			// On fait en sorte que le calendrier prenne toute la longueur de la page
			'& div, & span': {
				width: '100%',
				maxWidth: '100%',
				textAlign: 'center'
			},
			'& p': {
				...styles(theme).text0,
				fontSize: 16,
				lineHeight: '19px',
				textTransform: 'capitalize'
			},
			// On grise les dates désactivées
			'& .MuiPickersDay-dayDisabled p': {
				color: '#D9D9D9'
			},
			// On applique un style particulier pour l'entête du calendrier (mois & année)
			'& div[class*=MuiPickersCalendarHeader] p': {
				fontSize: 18,
				marginTop: 14,
				fontWeight: 'bold'
			},
			// On fait en sorte que le calendrier prenne toute la hauteur possible
			'& div[class*=MuiPickersBasePicker], & div[class*=MuiPickersCalendar-transitionContainer], & div[class*=MuiPickersCalendar-transitionContainer] > div': {
				justifyContent: 'center',
				height: '100%'
			},
			// Style pour chacune des lignes des semaines
			'& .MuiPickersCalendar-week': {
				height: 'calc(100% / 5.5)'
			},
			// Style pour la date sélectionnée
			'& .MuiPickersDay-daySelected p': {
				color: WHITE
			},
			// Si les boutons Précédent et Suivant du datepicker sont désactivés, on ne les affiche pas
			'& .MuiPickersCalendarHeader-switchHeader .MuiButtonBase-root.Mui-disabled': {
				display: 'none'
			},
			'& .MuiPickersSlideTransition-transitionContainer': {
				height: 48
			}
		},
		creneauxContainer: {
			width: '100%',
			height: '100%',
			maxWidth: props => props.isLargeScreen ? 132 : 'unset',
			minWidth: props => props.isLargeScreen ? 132 : 'unset',
			marginLeft: props => props.isLargeScreen ? 18 : 0,
			position: 'relative',

			'& div:nth-child(2)': {
				overflow: 'auto',
				height: '100%',
				paddingTop: 30,
				paddingBottom: 30,
				flexFlow: props => props.isLargeScreen ? 'wrap' : 'row',

				'&::-webkit-scrollbar': {
					width: 0
				}
			}
		},
		effetFlou: {
			position: 'absolute',
			width: props => props.isLargeScreen ? '100%' : 30,
			height: 56,
			left: 0,
			zIndex: 1
		},
		flouHaut: {
			top: 0,
			background: 'transparent linear-gradient(0deg, #FFFFFF00 0%, #FFFFFF 100%) 0% 0% no-repeat padding-box'
		},
		flouBas: {
			bottom: 0,
			background: 'transparent linear-gradient(180deg, #FFFFFF00 0%, #FFFFFF 100%) 0% 0% no-repeat padding-box'
		},
		effetFlouMobile: {
			position: 'absolute',
			width: props => props.isLargeScreen ? '100%' : 30,
			height: 56,
			top: 30,
			zIndex: 1
		},
		flouGauche: {
			left: 0,
			background: 'transparent linear-gradient(0deg, #FFFFFF00 0%, #FFFFFF 100%) 0% 0% no-repeat padding-box'
		},
		flouDroite: {
			right: 0,
			background: 'transparent linear-gradient(180deg, #FFFFFF00 0%, #FFFFFF 100%) 0% 0% no-repeat padding-box'
		},
		fabCalendrierVisite: {
			width: '100%',
			borderRadius: '21px !important',
			marginTop: 3,
			marginBottom: 3,
			boxShadow: 'none',
			minWidth: props => props.isLargeScreen ? 'inherit' : 80
		},
		fabCalendrierVisiteSelectionne: {
			background: `${theme.palette.primary.main} !important`
		},
		fabCalendrierVisiteNonSelectionne: {
			border: '1px solid #D9D9D9',
			background: `${WHITE} !important`
		},
		boutonContainer: {
			height: 49,
			marginTop: 15,

			'& button': {
				width: '100%',
				borderRadius: 25,
				position: 'relative',

				'& svg': {
					position: 'absolute',
					right: 10,
					top: '50%',
					transform: 'translateY(-50%)'
				}
			}
		},
		fabCalendrierDisabled: {
			border: '1px solid #D9D9D9 !important',
			background: `${WHITE} !important`,
			color: '#D9D9D9 !important'
		}
	})
)

interface AcceptingProps {
	onClose: () => void
}

type CalendrierVisiteProps = AcceptingProps

const CalendrierVisite: React.FC<CalendrierVisiteProps> = (
	{
		onClose
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const classes = useStyles({ isLargeScreen })
	const intl = useIntl()

	const {
		creneauxDisponibles,
		loadingCreneaux,
		loadingConfirmation,
		confirmationRendezVous,
		date,
		changeDate,
		creneau,
		setCreneau,
		minDate,
		maxDate,
	} = useContext(CalendrierContext)

	const dateSelectionneeFormattee = useMemo(() => formatageDate(date.toString(), 'yyyy-MM-dd'), [date])

	return (
		<Grid container item xs={12} className={classes.calendrierSubContainer}>
			{/* Titre + icône pour fermer la popin */}
			<Grid container item xs={12} alignItems="center" className={classes.enteteContainer}>
				<Grid container item xs className={classes.titre}>
					<FormattedMessage
						id="preparezArrivee.modalPriseRdvEdle.titre"
						defaultMessage="Choisissez votre date de rendez-vous"
						description="Titre du calendrier de la popin de prise de rendez-vous"
					/>
				</Grid>

				<Grid container item xs className={classes.iconStyle}>
					<CloseIcon onClick={onClose} />
				</Grid>
			</Grid>

			{
				loadingCreneaux ? (
					<Loader />
				) : (
					<>
						{/* Conteneur du calendrier & de l'heure */}
						<Grid container item xs={12} className={classes.timePicker}>
							<Grid container item xs={12} className={classes.dateContainer}>
								<MuiPickersUtilsProvider utils={MomentUtils} locale={intl.locale}>
									<DatePicker
										autoOk
										orientation="landscape"
										variant="static"
										openTo="date"
										value={date}
										disableToolbar
										onChange={(date) => {
											if (date) {
												changeDate(date.toDate())
											}
										}}
										minDate={minDate}
										maxDate={maxDate}
										shouldDisableDate={(day) => {
											if (day) {
												const dateFormatee = formatageDate(day.toString(), 'yyyy-MM-dd')

												// S'il n'y a pas de créneaux disponibles pour le jour en question, on bloque le jour
												return !(dateFormatee in creneauxDisponibles)
											}

											return true
										}}
									/>
								</MuiPickersUtilsProvider>
							</Grid>

							<Grid container item xs className={classes.creneauxContainer}>
								<div
									className={
										classnames(
											isLargeScreen ? classes.effetFlou : classes.effetFlouMobile,
											isLargeScreen ? classes.flouHaut : classes.flouGauche
										)
									}
								/>

								<Grid container item xs={12}>
									{
										(dateSelectionneeFormattee in creneauxDisponibles) && creneauxDisponibles[dateSelectionneeFormattee].length ?
											creneauxDisponibles[dateSelectionneeFormattee].map((creneauDispo) =>
												<Fab
													key={creneauDispo.horaire}
													onClick={() => setCreneau(creneauDispo.horaire)}
													label={formattageHeure(creneauDispo.horaire)}
													ariaLabel={formattageHeure(creneauDispo.horaire)}
													isSelected={creneau === creneauDispo.horaire}
													fabStyle={
														classnames(
															classes.fabCalendrierVisite,
															creneau === creneauDispo.horaire ? classes.fabCalendrierVisiteSelectionne : classes.fabCalendrierVisiteNonSelectionne
														)
													}
												/>
											)
											:
											<Fab
												onClick={() => {
												}}
												label="-"
												ariaLabel="-"
												isSelected={false}
												fabStyle={
													classnames(
														classes.fabCalendrierVisite,
														classes.fabCalendrierVisiteNonSelectionne,
														classes.fabCalendrierDisabled
													)
												}
												disabled
											/>
									}
								</Grid>

								<div
									className={
										classnames(
											isLargeScreen ? classes.effetFlou : classes.effetFlouMobile,
											isLargeScreen ? classes.flouBas : classes.flouDroite
										)
									}
								/>
							</Grid>
						</Grid>
					</>
				)
			}

			{/* Bloc bouton */}
			<Grid
				container
				item
				xs={12}
				className={classes.boutonContainer}
				justify="center"
			>
				{
					loadingConfirmation ? (
						<Loader size={30} />
					) : (
						<Button
							background="backgroundAccessibility"
							color="colorWhite"
							textStyle="text4"
							disabled={!creneau}
							onClick={confirmationRendezVous}
						>
							<FormattedMessage
								id="preparezArrivee.modalPriseRdvEdle.bouton"
								defaultMessage="Confirmer votre rendez-vous"
								description="Bouton pour prendre rdv"
							/>

							<ChevronRight />
						</Button>
					)
				}
			</Grid>
		</Grid>
	)

}

export default CalendrierVisite